import React, { useCallback, useMemo, useRef } from 'react';
import CardCommon from '@op/components/CardCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import useIntls from '@opm/hooks/useIntls';
import { cardIcon, tableIcon } from '@op/constants/chart';
import { DBO_TM } from '@op/utils/format';
import TableView from '@opm/pages/CompetitorsAdCreatives/CompetitorsAdCreativesTable/TableView';
import CardListView from '@opm/pages/CompetitorsAdCreatives/CompetitorsAdCreativesTable/CardListView';
import MediaPreviewModal, {
  useMediaPreviewModal,
} from '@opm/pages/CompetitorsAdCreatives/CompetitorsAdCreativesTable/MediaPreviewModal';
import { useExtInfo } from '@opm/pages/CompetitorsAdCreatives/hooks/useExtInfo';
import { ADSortOptions } from '@opm/pages/CompetitorsAdCreatives/constants';
import { gotoDownload } from '@opm/pages/Download';
import { useRelatedCreatives } from '../../../hooks';
import './index.scss';

interface IRelatedCreativesSectionProps {
  /** 上级 filter（含 game_id + topic + 详情页 AD* 筛选器合并结果） */
  filter: any;
}

/**
 * 详情页 Related Creatives 区域
 *
 * 数据结构对齐 /materials/list（接口 /creative_trends/related_creatives 复用语义）
 * 直接复用 Competitors Ad Creatives 的 CardListView + TableView 组件
 *
 * 注：
 * - 不展示 AI Tags / AI Core Content（CreativeTrends 详情页不要 AI tagging）
 * - TableView 必传的 extInfoMap / extInfoLoading / setSearchVideoTags 用空值占位
 */
export default function RelatedCreativesSection({
  filter,
}: IRelatedCreativesSectionProps) {
  const { intl } = useIntls();
  const tableRef = useRef<HTMLDivElement>(null);

  // ─── 数据 Hook ───
  const {
    cardRecords,
    tableRecords,
    total,
    maxStats,
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    loading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    sortBy,
    setSortBy,
    keyword,
    setKeyword,
    searchVideoTags,
    setSearchVideoTags,
    viewMode,
    setViewMode,
  } = useRelatedCreatives({ filter, defaultViewMode: 'card' });

  // ─── 媒体预览 ───
  const { visible, open, close, previewData } = useMediaPreviewModal();

  // ─── ext_info（AI Tags / AI Hook Summary / AI Core Analysis），仅 TableView 可见时拉取 ───
  // filter.game_id 是详情页单 game 维度，按需转为 gameIds 数组
  const gameIds = useMemo(() => {
    const id = filter?.game_id;
    if (!id) return [];
    return Array.isArray(id) ? id.filter(Boolean) : [id];
  }, [filter?.game_id]);

  const { extInfoMap, extInfoLoading } = useExtInfo({
    records: viewMode === 'table' ? tableRecords : [],
    gameIds,
  });

  // ─── 下载 ───
  const handleDownloadOnClick = useCallback(() => {
    gotoDownload({
      key: 'MarketingCreativeTrendsRelatedCreativesDownload',
      fileName: `Related_Creatives_${DBO_TM.timeTitle(filter)}`,
      filter: {
        ...filter,
        sort_item: sortBy?.by,
        sort_model: sortBy?.order,
        search_text: keyword,
        search_video_tags: searchVideoTags,
      },
    });
  }, [filter, sortBy, keyword, searchVideoTags]);

  return (
    <div className="dbom-ct-detail-creatives-section">
      <CardCommon
        className="dbom-ct-detail-creatives-card"
        operations={[cardIcon, tableIcon]}
        title={intl('Related Ads')}
        defaultOperation={viewMode}
        alwaysShowOperations
        hasDownload
        handleDownloadOnClick={handleDownloadOnClick}
        handleOperationsOnClick={(item: any) => setViewMode(item.symbol)}
      >
        <FilterBoxCommon
          className="dbom-ct-filter-box"
          left={[
            {
              key: 'sortBy',
              defaultValue: sortBy?.by,
              defaultParams: {
                options: ADSortOptions,
                onChange: (value: string) => {
                  setSortBy({ by: value, order: 'desc' });
                },
              },
            },
          ]}
          right={[
            {
              key: 'search',
              defaultValue: keyword,
              defaultParams: {
                style: { width: 270 },
                placeholder: intl('Search'),
                onSearch: (value: string) => {
                  setKeyword(value);
                },
                onClear: () => {
                  setKeyword('');
                },
              },
            },
          ]}
          onChange={() => { }}
        />
        {viewMode === 'table' ? (
          <TableView
            tableRef={tableRef}
            data={tableRecords}
            total={total}
            maxStats={maxStats}
            pageInfo={pageInfo}
            changePageInfo={changeTablePageInfo}
            loading={loading}
            sortBy={sortBy}
            setSortBy={setSortBy}
            type="video"
            open={open}
            extInfoMap={extInfoMap}
            extInfoLoading={extInfoLoading}
            setSearchVideoTags={setSearchVideoTags}
          />
        ) : (
          <CardListView
            data={cardRecords}
            isFirstLoading={isFirstLoading}
            isEmpty={isEmpty}
            isPaginationLoading={isPaginationLoading}
            nextPage={loadNextPage}
            open={open}
            vertical
          />
        )}
      </CardCommon>
      <MediaPreviewModal
        visible={visible}
        onClose={close}
        {...(previewData || {})}
      />
    </div>
  );
}
